// Pure clothoid-arc geometry for curvature-at-junction relations.
// No OpenLayers / external imports — unit-tested with `node --test`.
// `buildCurveWithMinRadius` and `curveToCoordinates` are moved VERBATIM from
// the original CurvatureAtJunctionArcHandler.js; do not edit their bodies.

/**
 * Decide the arc geometry for one relation. Pure: takes plain {x,y} points.
 * Returns { type, coordinates: number[][], fromCurve }.
 *   fromCurve === true only when a real clothoid curve was fitted — the glue
 *   uses that flag to reproduce the original property-copy asymmetry.
 */
// A straight movement has no fitted curve. We still emit a DENSIFIED collinear
// polyline rather than just the two endpoints: the overlap-separation offset needs
// interior vertices to bow an opposite-direction pair apart between the shared
// junction nodes (see arcOverlapOffset.js / offsetPolylineTapered, both no-op on a
// <3-point line). With only 2 points a straight overlapping pair draws exactly on
// top of itself and can't be told apart. Extra collinear points don't change the
// rendered shape until the offset bows them.
export const STRAIGHT_ARC_POINTS = 64;
function densifyStraight(from, to, n) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    out.push([from.x + (to.x - from.x) * t, from.y + (to.y - from.y) * t]);
  }
  return out;
}

export function computeArcCoordinates(from, to, via, curvatureAtJunction) {
  let curve = null;
  if (curvatureAtJunction !== 0) {
    const radiusM = 10000 / Math.abs(curvatureAtJunction);
    curve = buildCurveWithMinRadius(from, to, via, radiusM);
  }
  if (curve === null) {
    return {
      type: curvatureAtJunction === 0 ? "curvature-arc" : "curvature-arc-dummy",
      coordinates: densifyStraight(from, to, STRAIGHT_ARC_POINTS),
      fromCurve: false,
    };
  }
  return {
    type: "curvature-arc",
    coordinates: curveToCoordinates(curve).map((p) => [p.x, p.y]),
    fromCurve: true,
  };
}

// buildCurveWithMinRadius + curveToCoordinates below are moved VERBATIM from
// the original CurvatureAtJunctionArcHandler.js — do not edit (output parity).

/**
 * Build a composite curve (clothoid → circular arc → clothoid) between
 * pStart and pEnd, approaching pTarget, where curvature gradually increases
 * from zero to 1/minRadiusM, holds at that maximum curvature for a circular
 * arc section, then gradually decreases back to zero.
 *
 * The entry and exit clothoid angles are independent, allowing the circular
 * arc section to shift asymmetrically closer to pTarget when the approach
 * angles from pStart and pEnd differ.
 *
 * Returns an object with a .get(t) method compatible with Bezier.js,
 * or null if no valid curve can be constructed.
 */
function buildCurveWithMinRadius(pStart, pEnd, pTarget, minRadiusM) {
  function len(v) {
    return Math.hypot(v.x, v.y);
  }
  function unit(v) {
    const n = len(v);
    return n === 0 ? { x: 0, y: 0 } : { x: v.x / n, y: v.y / n };
  }
  function sub(a, b) {
    return { x: a.x - b.x, y: a.y - b.y };
  }
  function mul(a, s) {
    return { x: a.x * s, y: a.y * s };
  }
  function dot(a, b) {
    return a.x * b.x + a.y * b.y;
  }
  function cross(a, b) {
    return a.x * b.y - a.y * b.x;
  }

  // Entry/exit directions (toward pTarget)
  const dirStart = unit(sub(pTarget, pStart));
  const dirEnd = unit(sub(pTarget, pEnd));

  // Determine the turning angle at the junction
  // The total angle swept is the angle between dirStart and the reverse of dirEnd
  // (since dirEnd points from pEnd toward pTarget, the exit tangent is -dirEnd)
  const exitDir = mul(dirEnd, -1); // tangent direction at pEnd (going away from pTarget)
  const totalTurnAngle = Math.atan2(
    cross(dirStart, exitDir),
    dot(dirStart, exitDir),
  );

  // Determine turn direction: +1 = left (CCW), -1 = right (CW)
  const turnSign = totalTurnAngle >= 0 ? 1 : -1;
  const absTotalAngle = Math.abs(totalTurnAngle);

  if (absTotalAngle < 1e-6) {
    // Essentially straight, no curve needed
    return null;
  }

  const R = minRadiusM;

  // We split the curve into 3 sections with independent entry/exit angles:
  //   1. Entry clothoid:  curvature 0 → 1/R over angle α₁
  //   2. Circular arc:    curvature = 1/R over angle β
  //   3. Exit clothoid:   curvature 1/R → 0 over angle α₂
  // Constraint: α₁ + α₂ + β = absTotalAngle
  //
  // For a clothoid sweeping angle α, arc length L = 2·R·α
  //
  // We search over independent fractions f1, f2 ∈ (0, ~0.5)
  // where α₁ = f1 · absTotalAngle, α₂ = f2 · absTotalAngle,
  // β = (1 - f1 - f2) · absTotalAngle.

  // --- Helper functions for curve sections ---

  // Entry clothoid: curvature linearly increases from 0 to 1/R
  function clothoidPoints(startPos, startAngle, sign, alpha, radius, numPts) {
    const L = 2 * radius * alpha;
    const points = [];
    const steps = numPts - 1;
    let x = startPos.x;
    let y = startPos.y;
    let heading = startAngle;
    points.push({ x, y });

    for (let i = 1; i <= steps; i++) {
      const tPrev = (i - 1) / steps;
      const tCurr = i / steps;
      const ds = L * (tCurr - tPrev);
      // Curvature at midpoint: linearly from 0 to 1/R → κ(t) = t/R
      const tMid = (tPrev + tCurr) / 2;
      const curvature = (sign * tMid) / radius;
      heading += curvature * ds;
      x += Math.cos(heading) * ds;
      y += Math.sin(heading) * ds;
      points.push({ x, y });
    }
    return { points, endPos: { x, y }, endHeading: heading };
  }

  // Circular arc: constant curvature 1/R
  function arcPoints(startPos, startAngle, sign, arcAngle, radius, numPts) {
    const points = [];
    const curvature = sign / radius;
    const arcLength = Math.abs(arcAngle) * radius;
    const steps = numPts - 1;
    let x = startPos.x;
    let y = startPos.y;
    let heading = startAngle;
    points.push({ x, y });

    for (let i = 1; i <= steps; i++) {
      const ds = arcLength / steps;
      heading += curvature * ds;
      x += Math.cos(heading) * ds;
      y += Math.sin(heading) * ds;
      points.push({ x, y });
    }
    return { points, endPos: { x, y }, endHeading: heading };
  }

  // Exit clothoid: curvature linearly decreases from 1/R to 0
  function clothoidPointsReverse(
    startPos,
    startAngle,
    sign,
    alpha,
    radius,
    numPts,
  ) {
    const L = 2 * radius * alpha;
    const points = [];
    const steps = numPts - 1;
    let x = startPos.x;
    let y = startPos.y;
    let heading = startAngle;
    points.push({ x, y });

    for (let i = 1; i <= steps; i++) {
      const tPrev = (i - 1) / steps;
      const tCurr = i / steps;
      const ds = L * (tCurr - tPrev);
      // Curvature at midpoint: linearly from 1/R to 0 → κ(t) = (1-t)/R
      const tMid = (tPrev + tCurr) / 2;
      const curvature = (sign * (1 - tMid)) / radius;
      heading += curvature * ds;
      x += Math.cos(heading) * ds;
      y += Math.sin(heading) * ds;
      points.push({ x, y });
    }
    return { points, endPos: { x, y }, endHeading: heading };
  }

  // --- Geometry helpers for intersection / inner-side checks ---

  // Determine the "inner side" sign for a point relative to a directed segment.
  // The inner side of the turn pStart→pTarget→pEnd is the side where the curve
  // should be. We compute which side of each segment the curve should stay on.
  // Returns: for segment A→B, the sign of cross(B-A, P-A) that a point P on
  // the inner side should have.
  const innerSignSeg1 = Math.sign(
    cross(sub(pTarget, pStart), sub(pEnd, pStart)),
  );
  // For segment pTarget→pEnd, use pStart as the inner-side reference
  const innerSignSeg2 = Math.sign(
    cross(sub(pEnd, pTarget), sub(pStart, pTarget)),
  );

  // Check if a point is on the inner side (or on) segment pStart→pTarget
  function isInnerSideSeg1(p) {
    const s = cross(sub(pTarget, pStart), sub(p, pStart));
    return (
      innerSignSeg1 === 0 ||
      Math.sign(s) === innerSignSeg1 ||
      Math.abs(s) < 1e-20
    );
  }

  // Check if a point is on the inner side (or on) segment pTarget→pEnd
  function isInnerSideSeg2(p) {
    const s = cross(sub(pEnd, pTarget), sub(p, pTarget));
    return (
      innerSignSeg2 === 0 ||
      Math.sign(s) === innerSignSeg2 ||
      Math.abs(s) < 1e-20
    );
  }

  // Count how many segments of the curve polyline intersect the boundary
  // segments pStart→pTarget and pTarget→pEnd, and how many interior points
  // lie on the outer side of the turn.
  // Returns { intersectionCount: number, outerCount: number }
  function checkCurveAgainstBoundary(fittedPoints) {
    let outerCount = 0;

    // Count how many interior points are on the outer side
    for (let i = 1; i < fittedPoints.length - 1; i++) {
      const p = fittedPoints[i];
      if (!isInnerSideSeg1(p) || !isInnerSideSeg2(p)) {
        outerCount++;
      }
    }

    return outerCount;
  }

  const startHeading = Math.atan2(dirStart.y, dirStart.x);

  let bestCurve = null;
  let bestScore = Infinity;

  const gridSteps = 20; // grid resolution for f1 and f2
  const ptsPerSection = 40;

  // Search over independent entry/exit clothoid fractions
  for (let i1 = 1; i1 <= gridSteps; i1++) {
    const f1 = i1 / (gridSteps * 2); // f1 from 0.025 to 0.50
    for (let i2 = 1; i2 <= gridSteps; i2++) {
      const f2 = i2 / (gridSteps * 2); // f2 from 0.025 to 0.50

      // Circular arc fraction must be non-negative
      // There should be a minimal circular angle
      if (f1 + f2 >= 1) continue;
      // There should be a minimal entry and exit
      // if (f1 < 0.1) continue;
      // if (f2 < 0.1) continue;

      const alphaEntry = f1 * absTotalAngle;
      const alphaExit = f2 * absTotalAngle;
      const beta = (1 - f1 - f2) * absTotalAngle;

      // Iteratively compensate the construction radius so that after the
      // scale+rotate fit the actual arc-section radius equals the target R.
      // We start with Rcomp = R, build the curve, compute the scale factor,
      // then set Rcomp = R / scale and repeat until convergence.
      let Rcomp = R;
      let allPoints = null;
      let scale = 1;
      let rotAngle = 0;
      const maxIter = 5;

      const desDx = pEnd.x - pStart.x;
      const desDy = pEnd.y - pStart.y;
      const desLen = Math.hypot(desDx, desDy);
      const desAngle = Math.atan2(desDy, desDx);

      let valid = true;
      for (let iter = 0; iter < maxIter; iter++) {
        // Build entry clothoid (curvature 0 → 1/Rcomp)
        const entry = clothoidPoints(
          pStart,
          startHeading,
          turnSign,
          alphaEntry,
          Rcomp,
          ptsPerSection,
        );

        // Build circular arc (curvature = 1/Rcomp)
        const arc = arcPoints(
          entry.endPos,
          entry.endHeading,
          turnSign,
          beta,
          Rcomp,
          ptsPerSection,
        );

        // Build exit clothoid (curvature 1/Rcomp → 0)
        const exit = clothoidPointsReverse(
          arc.endPos,
          arc.endHeading,
          turnSign,
          alphaExit,
          Rcomp,
          ptsPerSection,
        );

        // Collect all points
        allPoints = [
          ...entry.points,
          ...arc.points.slice(1),
          ...exit.points.slice(1),
        ];

        const rawStart = allPoints[0];
        const rawEnd = allPoints[allPoints.length - 1];
        const rawDx = rawEnd.x - rawStart.x;
        const rawDy = rawEnd.y - rawStart.y;
        const rawLen = Math.hypot(rawDx, rawDy);

        if (rawLen < 1e-12) {
          valid = false;
          break;
        }

        const rawAngle = Math.atan2(rawDy, rawDx);
        scale = desLen / rawLen;
        rotAngle = desAngle - rawAngle;

        // Compensate: we want the fitted radius to be R,
        // so the construction radius should be R / scale.
        const newRcomp = R / scale;
        if (Math.abs(newRcomp - Rcomp) / R < 1e-6) break; // converged
        Rcomp = newRcomp;
      }

      if (!valid || !allPoints) continue;

      // Simulate the scale+rotate fitting to evaluate against actual geometry
      const cosRot = Math.cos(rotAngle);
      const sinRot = Math.sin(rotAngle);
      const rawStart = allPoints[0];

      // Transform all points to fitted positions
      const fittedPoints = allPoints.map((p) => {
        const dx = (p.x - rawStart.x) * scale;
        const dy = (p.y - rawStart.y) * scale;
        return {
          x: pStart.x + dx * cosRot - dy * sinRot,
          y: pStart.y + dx * sinRot + dy * cosRot,
        };
      });

      // Check intersection and inner-side constraints
      const outerCount = checkCurveAgainstBoundary(fittedPoints);

      // Find the closest distance to pTarget after fitting
      let minDistToTarget = Infinity;
      for (const p of fittedPoints) {
        const d = Math.hypot(p.x - pTarget.x, p.y - pTarget.y);
        if (d < minDistToTarget) minDistToTarget = d;
      }

      // Penalty-based scoring: outer-side points
      // are bad, and distance to pTarget is the tiebreaker.
      const outerPenalty = outerCount * 1e6;
      const score = minDistToTarget + outerPenalty;

      if (score < bestScore) {
        bestScore = score;
        bestCurve = allPoints;
      }
    }
  }

  // Apply scale + rotate transform to exactly match pStart → pEnd
  if (bestCurve && bestCurve.length > 2) {
    const rawStart = bestCurve[0];
    const rawEnd = bestCurve[bestCurve.length - 1];

    const rawDx = rawEnd.x - rawStart.x;
    const rawDy = rawEnd.y - rawStart.y;
    const rawLen = Math.hypot(rawDx, rawDy);
    const rawAngle = Math.atan2(rawDy, rawDx);

    const desDx = pEnd.x - pStart.x;
    const desDy = pEnd.y - pStart.y;
    const desLen = Math.hypot(desDx, desDy);
    const desAngle = Math.atan2(desDy, desDx);

    if (rawLen < 1e-12) return null;

    const scale = desLen / rawLen;
    const rotAngle = desAngle - rawAngle;
    const cosRot = Math.cos(rotAngle);
    const sinRot = Math.sin(rotAngle);

    const fitted = bestCurve.map((p) => {
      let dx = (p.x - rawStart.x) * scale;
      let dy = (p.y - rawStart.y) * scale;
      return {
        x: pStart.x + dx * cosRot - dy * sinRot,
        y: pStart.y + dx * sinRot + dy * cosRot,
      };
    });

    // Return a curve-like object with .get(t) for compatibility
    return {
      _points: fitted,
      get(t) {
        const clampedT = Math.max(0, Math.min(1, t));
        const idx = clampedT * (this._points.length - 1);
        const lo = Math.floor(idx);
        const hi = Math.min(lo + 1, this._points.length - 1);
        const frac = idx - lo;
        return {
          x:
            this._points[lo].x +
            frac * (this._points[hi].x - this._points[lo].x),
          y:
            this._points[lo].y +
            frac * (this._points[hi].y - this._points[lo].y),
        };
      },
    };
  }

  return null;
}
function curveToCoordinates(curve, samples = 100) {
  return Array.from(Array(samples).keys()).map((index) =>
    curve.get(index / samples),
  );
}
